import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, PanInfo, animate, useMotionValue, usePresence, useTransform } from 'framer-motion';

export interface LaunchOrigin {
  x: number;
  y: number;
  w: number;
  h: number;
  radius: number;
}

const Window = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 20;
  overflow: hidden;
  background: #000;
`;

const Content = styled(motion.div)`
  position: absolute;
  inset: 0;
`;

const Splash = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5%;
  overflow: hidden;
`;

const SplashBackdrop = styled.div<{ $src?: string }>`
  position: absolute;
  inset: -30%;
  background: ${props => (props.$src ? `url(${props.$src}) center / cover no-repeat` : '#1c1c1e')};
  filter: blur(40px) saturate(1.6) brightness(0.75);
  transform: scale(1.2);
`;

const SplashIcon = styled.div<{ $src?: string }>`
  position: relative;
  width: 32%;
  aspect-ratio: 1;
  border-radius: 22.5%;
  background: ${props => (props.$src ? `#fff url(${props.$src}) center / cover no-repeat` : '#2c2c2e')};
  box-shadow: 0 2cqw 6cqw rgba(0, 0, 0, 0.35);
`;

const SplashName = styled(motion.span)`
  position: relative;
  color: #fff;
  font-size: 5cqw;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-shadow: 0 0.4cqw 2cqw rgba(0, 0, 0, 0.35);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif;
`;

const Stage = styled(motion.div)`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #000;
`;

const Backdrop = styled(motion.div)`
  position: absolute;
  inset: -20%;
  background-position: center;
  background-size: cover;
  filter: blur(30px) saturate(1.4) brightness(0.7);
`;

const Shot = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: grab;
  touch-action: pan-y;

  &:active {
    cursor: grabbing;
  }
`;

const Dots = styled.div`
  position: absolute;
  left: 50%;
  bottom: 6.5cqw;
  transform: translateX(-50%);
  display: flex;
  gap: 1.4cqw;
  padding: 1.2cqw 2cqw;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(8px);
  z-index: 5;
`;

const Dot = styled.span<{ $active: boolean }>`
  height: 1.5cqw;
  width: ${props => (props.$active ? '4.5cqw' : '1.5cqw')};
  border-radius: 1cqw;
  background: #fff;
  opacity: ${props => (props.$active ? 1 : 0.45)};
  transition: width 0.35s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.35s ease;
`;

const Touch = styled(motion.span)`
  position: absolute;
  top: 58%;
  width: 11cqw;
  height: 11cqw;
  margin: -5.5cqw 0 0 -5.5cqw;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  border: 0.4cqw solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 0 4cqw rgba(0, 0, 0, 0.35);
  z-index: 6;
  pointer-events: none;
`;

const slide = {
  enter: (direction: number) => ({ x: direction > 0 ? '100%' : '-100%' }),
  center: { x: '0%', opacity: 1, scale: 1 },
  exit: (direction: number) => ({ x: direction > 0 ? '-30%' : '30%', opacity: 0.5, scale: 0.96 }),
};

// iOS-like: no bounce, settles in ~half a second.
const settle = { type: 'spring' as const, duration: 0.5, bounce: 0 };

interface AppWindowProps {
  name: string;
  icon?: string;
  screenshots: string[];
  origin: LaunchOrigin;
  running: boolean;
  paused: boolean;
  onScreenChange?: (index: number, total: number) => void;
  onComplete?: () => boolean;
  onInteract?: () => void;
}

const SLIDE_MS = 2800;

const AppWindow: React.FC<AppWindowProps> = ({
  name,
  icon,
  screenshots,
  origin,
  running,
  paused,
  onScreenChange,
  onComplete,
  onInteract,
}) => {
  const [isPresent, safeToRemove] = usePresence();
  const progress = useMotionValue(0);
  const [[index, direction], setPage] = useState<[number, number]>([0, 1]);
  const [touchKey, setTouchKey] = useState(0);
  const total = screenshots.length;
  const callbacks = useRef({ onScreenChange, onComplete });
  callbacks.current = { onScreenChange, onComplete };

  const paginate = (step: number) => {
    setPage(([current]) => [(current + step + total) % total, step]);
  };

  useEffect(() => {
    if (running && total) callbacks.current.onScreenChange?.(index, total);
  }, [index, running, total]);

  // Auto-advance, simulating a user swiping through the app.
  useEffect(() => {
    if (!running || paused || total < 2) return;
    const timer = setTimeout(() => {
      if (index === total - 1 && callbacks.current.onComplete?.()) return;
      setTouchKey(key => key + 1);
      setTimeout(() => paginate(1), 160);
    }, SLIDE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, paused, index, total]);

  // Preload the next screenshot so swipes never flash.
  useEffect(() => {
    if (!total) return;
    const next = new Image();
    next.src = screenshots[(index + 1) % total];
  }, [index, screenshots, total]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    onInteract?.();
    if (info.offset.x < -40 || info.velocity.x < -400) paginate(1);
    else if (info.offset.x > 40 || info.velocity.x > 400) paginate(-1);
  };

  // One progress value drives the whole launch: the window is clipped out of the icon's rect while
  // its content travels from the icon's position and scale, so the splash icon starts exactly on top
  // of the home-screen icon. Closing runs the same path backwards before the window unmounts.
  useEffect(() => {
    const controls = isPresent
      ? animate(progress, 1, settle)
      : animate(progress, 0, { ...settle, duration: 0.42, onComplete: () => safeToRemove?.() });
    return () => controls.stop();
  }, [isPresent, progress, safeToRemove]);

  const mix = (from: number, to: number, p: number) => from + (to - from) * p;
  const right = 100 - origin.x - origin.w;
  const bottom = 100 - origin.y - origin.h;
  const clipPath = useTransform(
    progress,
    p => `inset(${mix(origin.y, 0, p)}% ${mix(right, 0, p)}% ${mix(bottom, 0, p)}% ${mix(origin.x, 0, p)}% round ${mix(origin.radius, 0, p)}px)`
  );
  const x = useTransform(progress, p => `${mix(origin.x + origin.w / 2 - 50, 0, p)}%`);
  const y = useTransform(progress, p => `${mix(origin.y + origin.h / 2 - 50, 0, p)}%`);
  const scale = useTransform(progress, p => mix(origin.w / 32, 1, p));
  const opacity = useTransform(progress, [0, 0.12], [0, 1]);

  return (
    <Window style={{ clipPath, opacity }}>
      <Content style={{ x, y, scale }}>
        <Splash>
          <SplashBackdrop $src={icon} />
          <SplashIcon $src={icon} />
          <SplashName initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.25 }}>
            {name}
          </SplashName>
        </Splash>

        <AnimatePresence>
          {running && total > 0 && (
            <Stage key="stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: 'easeOut' }}>
              <AnimatePresence initial={false}>
                <Backdrop
                  key={`bg-${index}`}
                  style={{ backgroundImage: `url(${screenshots[index]})` }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                />
              </AnimatePresence>

              <AnimatePresence initial={false} custom={direction}>
                <Shot
                  key={index}
                  src={screenshots[index]}
                  alt={`${name} screen ${index + 1}`}
                  draggable={false}
                  custom={direction}
                  variants={slide}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: 'spring', duration: 0.45, bounce: 0 }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.8}
                  onDragEnd={onDragEnd}
                />
              </AnimatePresence>

              <AnimatePresence>
                {touchKey > 0 && (
                  <Touch
                    key={touchKey}
                    initial={{ left: '74%', opacity: 0, scale: 0.6 }}
                    animate={{ left: ['74%', '74%', '26%', '26%'], opacity: [0, 1, 1, 0], scale: [0.6, 1, 1, 0.8] }}
                    transition={{ duration: 0.75, times: [0, 0.2, 0.75, 1], ease: 'easeInOut' }}
                  />
                )}
              </AnimatePresence>

              {total > 1 && (
                <Dots>
                  {screenshots.map((_, dot) => (
                    <Dot key={dot} $active={dot === index} />
                  ))}
                </Dots>
              )}
            </Stage>
          )}
        </AnimatePresence>
      </Content>
    </Window>
  );
};

export default AppWindow;
